import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PunctualityStatsSummary, ScheduleSlotSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type SlotDraft = {
  startsAt: string;
  endsAt: string;
  note: string;
};

const emptyDraft = (): SlotDraft => ({
  startsAt: '',
  endsAt: '',
  note: '',
});

export function ModelScheduleCard(): ReactElement {
  const { api } = useAuth();
  const [slots, setSlots] = useState<ScheduleSlotSummary[]>([]);
  const [punctuality, setPunctuality] = useState<PunctualityStatsSummary | null>(null);
  const [draft, setDraft] = useState<SlotDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.listMySchedule();
    setSlots(result.slots);
    setPunctuality(result.punctuality);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.schedule.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function handleAdd(): Promise<void> {
    if (!draft.startsAt || !draft.endsAt) {
      setError(t('common.studio.schedule.invalidItem'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createScheduleSlot({
        startsAt: new Date(draft.startsAt).toISOString(),
        endsAt: new Date(draft.endsAt).toISOString(),
        note: draft.note.trim() || null,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.schedule.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(slotId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteScheduleSlot(slotId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.schedule.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.schedule.loading')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.schedule.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.schedule.hint')}
      </p>

      {punctuality ? (
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
          {t('common.studio.schedule.reliability')
            .replace('{{percent}}', String(punctuality.reliabilityPercent))
            .replace('{{label}}', punctuality.reliabilityLabel)}
        </p>
      ) : null}

      {error ? <p className="fm-error">{error}</p> : null}

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {slots.map((slot) => (
          <li
            key={slot.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
            }}
          >
            <span style={{ flex: '1 1 12rem', fontWeight: 600, fontSize: '0.875rem' }}>
              {new Date(slot.startsAt).toLocaleString()} → {new Date(slot.endsAt).toLocaleString()}
              {slot.enabled ? '' : ` · ${t('common.studio.schedule.disabled')}`}
            </span>
            <Button
              label={t('common.studio.schedule.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(slot.id);
              }}
            />
          </li>
        ))}
      </ul>

      {slots.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.schedule.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem' }}>
        <label style={{ fontSize: '0.875rem' }}>
          {t('common.studio.schedule.startsAt')}
          <input
            type="datetime-local"
            value={draft.startsAt}
            onChange={(event) => setDraft((prev) => ({ ...prev, startsAt: event.target.value }))}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ fontSize: '0.875rem' }}>
          {t('common.studio.schedule.endsAt')}
          <input
            type="datetime-local"
            value={draft.endsAt}
            onChange={(event) => setDraft((prev) => ({ ...prev, endsAt: event.target.value }))}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label style={{ fontSize: '0.875rem' }}>
          {t('common.studio.schedule.note')}
          <input
            type="text"
            value={draft.note}
            onChange={(event) => setDraft((prev) => ({ ...prev, note: event.target.value }))}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.schedule.saving') : t('common.studio.schedule.add')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
